/** Expense types listed separately from receipt rows in `xpense-list.js`. */
export const NON_RECEIPT_EXPENSE_TYPES = ['mileage'];

/**
 * @param {{ json?: { type?: string } }} expense
 * @returns {boolean}
 *
 * Returns true for explicit "receipt" type and for legacy null-typed expenses —
 * null is not in NON_RECEIPT_EXPENSE_TYPES so both are treated as receipt rows.
 * XPEN-2658: once the API enforces the allowlist, null can only appear on legacy data.
 */
export function isReviewableReceiptExpense(expense) {
  return !NON_RECEIPT_EXPENSE_TYPES.includes(expense?.json?.type);
}

/**
 * Receipt expenses in account list order (MongoDB $push / upload order).
 * @param {Array<{ checksum?: string, json?: { type?: string } }>} expenses
 */
export function getReviewableExpenses(expenses) {
  return (expenses || []).filter(isReviewableReceiptExpense);
}

/**
 * 0-based index of the current expense among reviewable receipts, for "Expense N" headings.
 * @returns {number}
 */
export function getReviewableExpenseIndex(expenses, checksum) {
  const list = getReviewableExpenses(expenses);
  const index = list.findIndex((expense) => expense.checksum === checksum);
  return index >= 0 ? index : 0;
}

/** Statuses from which manually validating a single expense line makes sense —
 * AI processing has finished (Completed) or flagged something for a human to
 * confirm (Need_Attention). A whitelist, not a blacklist: the backend has no
 * status-transition guard of its own (XPEN-3027-001), so any status this function
 * does not explicitly recognize must fail closed rather than default to allowed. */
const VALIDATABLE_EXPENSE_STATUSES = ['Completed', 'Need_Attention'];

/**
 * @param {string} status - an expense's current `status` field.
 * @returns {boolean} true only when validating from this exact status is safe.
 */
export function canValidateExpense(status) {
  return VALIDATABLE_EXPENSE_STATUSES.includes(status);
}

/** Statuses a report can only reach once it has been submitted. A whitelist for
 * the same reason as VALIDATABLE_EXPENSE_STATUSES above: a status this list does
 * not recognize must fail closed.
 *
 * `Pending_Exception_Approval` is absent, and that is the point of XPEN-3383:
 * submission is refused while exception issues are open (_submit_expense_account
 * returns 421 before writing `submitted_at`), so a report awaiting an exception
 * decision has never been submitted. Management confirmed on 7 September 2026 that
 * validation appears only once every exception has been approved, at which point
 * submission runs.
 *
 * Honest limit: `Validated` is reachable on a never-submitted report today, because
 * the server does not yet refuse validation on one — the line roll-up then promotes
 * the account. XPEN-3384 closes that. */
const VALIDATABLE_ACCOUNT_STATUSES = [
  'Submitted',
  'Validated',
  'Need_Approval',
  'Approved',
  'Denied',
  'Finalized',
];

/**
 * Whether an expense report has reached a state where validating its lines makes
 * sense (XPEN-3383).
 *
 * Both halves are load-bearing.
 *
 * `submitted_at` is written on every successful submit and cleared nowhere, so it
 * cannot be wrong when present. It is needed because a submitted report CAN be
 * forced back to `Pending`: editing a line that still carries an approved
 * `exception_approval` record clears it, and with no such record left
 * `resolve_exception_account_state` sets the account to `Pending`
 * (exception_approval_manager). Without this branch a validator would lose the
 * controls on a legitimately submitted report the moment anyone edited that line.
 *
 * The status list covers reports predating the stamp (30 April 2024).
 *
 * Takes the whole report rather than a status string because it needs both fields.
 * Deliberately not named `canValidate*`: `canValidateExpense(status)` above,
 * `AccessControl.userCanValidateExpenses(data)`, and a local `canValidate` boolean
 * already coexist in these files.
 *
 * A visibility gate, not a security boundary — the server does not check the
 * report's state on the validate route. XPEN-3384 covers that.
 *
 * @param {object} expensesAccount - the expense report, as sent to the browser.
 * @returns {boolean} true when the report is far enough along to be validated.
 */
export function isAccountOpenForValidation(expensesAccount) {
  // Checked before the stamp, not after. A report can carry `submitted_at` AND be
  // awaiting an exception decision: once it has been forced back to `Pending` (see
  // above), the owner can request a second exception cycle, and neither
  // `request_exception_approval` nor the status write clears the stamp. Reading
  // `submitted_at` first would show the controls while exceptions are outstanding,
  // which is the one thing the 7 September rule forbids.
  if (expensesAccount?.status === 'Pending_Exception_Approval') return false;
  if (expensesAccount?.submitted_at) return true;
  return VALIDATABLE_ACCOUNT_STATUSES.includes(expensesAccount?.status);
}
